// Global search (Ctrl+K / Cmd+K): pages from the sidebar plus records from GET /api/v1/search.
import { useNavigate } from '@tanstack/react-router'
import { CornerDownLeft, FileSearch } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Spinner } from '../components/states'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { keyOfHit } from '../lib/endpoints'
import { useDebounced } from '../lib/hooks'
import { useGlobalSearch, useSchema } from '../lib/queries'
import { DEFAULT_NAV_ICON, NAV_ICONS } from './nav-icons'
import { useNavSections } from './Sidebar'

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const term = useDebounced(q, 200)
  const search = useGlobalSearch(term)
  const { data: schema } = useSchema()
  const sections = useNavSections()
  const pages = sections.flatMap((s) => s.items)
  const needle = q.trim().toLowerCase()
  const matchingPages = needle ? pages.filter((p) => p.label.toLowerCase().includes(needle)) : pages

  useEffect(() => {
    if (!open) setQ('')
  }, [open])

  const go = (to: string) => {
    onOpenChange(false)
    void navigate({ to })
  }

  const groups = term.trim().length >= 2 ? (search.data ?? []) : []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        hideClose
        className="top-[12vh] overflow-hidden p-0"
        aria-describedby="cmdk-help"
      >
        <DialogTitle className="sr-only">Search Clearpol Compliance</DialogTitle>
        <DialogDescription id="cmdk-help" className="sr-only">
          Type to search records across the platform, or jump to a page. Use the arrow keys and Enter.
        </DialogDescription>
        <Command shouldFilter={false} loop>
          <CommandInput
            value={q}
            onValueChange={setQ}
            placeholder="Search controls, policies, evidence, vendors, people..."
            autoFocus
          />
          <CommandList>
            {search.isFetching && groups.length === 0 ? (
              <div className="flex justify-center py-4">
                <Spinner label="Searching" />
              </div>
            ) : null}
            {term.trim().length >= 2 &&
            !search.isFetching &&
            groups.length === 0 &&
            matchingPages.length === 0 ? (
              <CommandEmpty>No results for "{term.trim()}".</CommandEmpty>
            ) : null}
            {matchingPages.length ? (
              <CommandGroup heading="Pages">
                {matchingPages.slice(0, needle ? 6 : 20).map((p) => {
                  const Icon = (typeof p.icon === 'string' && NAV_ICONS[p.icon]) || DEFAULT_NAV_ICON
                  return (
                    <CommandItem key={`page-${p.key}`} value={`page-${p.key}`} onSelect={() => go(p.to)}>
                      <Icon className="text-ink-subtle" aria-hidden />
                      {p.label}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ) : null}
            {groups.map((g) => {
              const entity = schema?.entity(g.entity)
              const plural = g.plural ?? entity?.plural
              return (
                <CommandGroup
                  key={g.entity}
                  heading={`${g.label}${g.total > g.items.length ? ` (${g.total})` : ''}`}
                >
                  {g.items.map((hit) => {
                    const key = keyOfHit(hit, entity?.key)
                    return (
                      <CommandItem
                        key={`${g.entity}-${hit.id}`}
                        value={`${g.entity}-${hit.id}`}
                        onSelect={() => plural && go(`/${plural}/${encodeURIComponent(hit.id)}`)}
                      >
                        <FileSearch className="text-ink-subtle" aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{hit.display}</span>
                        {key && key !== hit.display ? (
                          <span className="font-mono text-xs text-ink-subtle">{key}</span>
                        ) : null}
                      </CommandItem>
                    )
                  })}
                  {g.total > g.items.length && plural ? (
                    <CommandItem
                      value={`${g.entity}-all`}
                      onSelect={() => go(`/${plural}?q=${encodeURIComponent(term.trim())}`)}
                    >
                      <CornerDownLeft className="text-ink-subtle" aria-hidden />
                      See all {g.total} matching {g.label.toLowerCase()}
                    </CommandItem>
                  ) : null}
                </CommandGroup>
              )
            })}
          </CommandList>
          <div className="flex items-center justify-between border-t border-line px-3 py-2 text-xs text-ink-subtle">
            <span>Search covers every record you can read.</span>
            <span>
              <kbd className="rounded border border-line px-1">Esc</kbd> to close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
